import { Hand, MousePointer2 } from '@/components/ui/icons'
import { FLUX3_TESTS } from '@/lib/flux3/tests.generated'

// One 14 s choreography, run twice at once on the same canvas: 90 clips, every one a different
// video, scattered in loose bins the way a real production canvas ends up. Left ("Other
// canvases"): hovering does nothing, clicking play spins forever, half the tiles are still
// loading, zoom and pan land in steps. Right (Martini): every clip scrubs under the cursor the
// moment it is hovered, zoom and pan are eased, 60 fps. Static under prefers-reduced-motion.
//
// Units: the panel is 16:10 and 100 units wide (so 62.5 tall); the world is 3× the panel. Zooms
// are anchored at the cursor that drives them (T = C − s·P for the world point P under the cursor
// at panel point C), so the zoomed-out view (scale ⅓, T = 0) and each close-up (scale 1) follow
// from where the scrubbed clip sits. Cursors animate x and y on nested wrappers with different
// easings, which is what makes the paths arc instead of sliding along straight lines.
//
//   0.0– 0.5  Editor enters clip S1, pauses          2.6– 5.0  zoom out at the Editor's cursor
//   0.8– 2.0  Editor scrubs S1 (left: clicks play)   5.4– 7.9  Director arcs to S2, scrubs it zoomed out
//   8.2–10.2  zoom in at the Director's cursor      10.5–12.0  Director scrubs S3 close up
//  10.6–11.6  Claude Code reaches for the canvas    12.0–13.3  Claude Code drags the canvas back to S1
//  12.8–13.8  Editor returns to S1

const LOOP = 14
const WORLD_W = 300
const WORLD_H = 187.5
const PANEL_H = 62.5
const small = (poster: string) => poster.replace('/1080.webp', '/480.webp')

// ---------------------------------------------------------------------------------------------
// Images: three scrubbed clips flip through their own pinned keyframes; every other FLUX.3
// poster once, minus frames that are mostly text or near-black, plus the curated marketing stills.
// ---------------------------------------------------------------------------------------------
const CLIPS = FLUX3_TESTS.flatMap(p => p.clips)
const SCRUBS = ['K8a', 'K5i', 'K7a'].map(id => {
  const c = CLIPS.find(c => c.title.startsWith(id))
  if (!c?.poster || !c.keyframes) throw new Error(`ProductionPerformance: scrub clip ${id} missing`)
  // ponytail: six of the pins, evenly spaced; every frame is a ~350 KB JPEG and all are preloaded.
  const n = c.keyframes.length
  return {
    poster: c.poster,
    frames: Array.from({ length: 6 }, (_, i) => c.keyframes![Math.round((i * (n - 1)) / 5)].image),
  }
})
const SKIP = ['E12-051', 'E12-052', 'E12-011', 'E12-047', 'E2-007']
const seen = new Set<string>(SCRUBS.map(s => s.poster))
const POOL: string[] = []
for (const page of FLUX3_TESTS)
  for (const c of [page.hero, ...page.clips]) {
    if (!c?.poster || seen.has(c.poster) || SKIP.some(s => c.title.startsWith(s))) continue
    seen.add(c.poster)
    POOL.push(small(c.poster))
  }
for (const n of [
  'atlantis',
  'boy',
  'choibokThumb',
  'feet',
  'gamiThumb',
  'HWM',
  'indie',
  'jupiter',
  'lastday',
  'murcury-mars',
  'mylifeline',
])
  POOL.push(`/marketing/thumbs/${n}.jpg`)
for (const n of ['between-the-lines', 'emptyvessels', 'geely-ex5', 'MechaSailors', 'Ride', 'SomeoneMissesYourCall'])
  POOL.push(`/marketing/thumbs/new/${n}.jpg`)
// ponytail: stride 37 spreads the stills among the posters; any step coprime with the pool size works.
const ORDER = POOL.map((_, i) => POOL[(i * 37) % POOL.length])

// ---------------------------------------------------------------------------------------------
// Layout: nine loose groups, row-packed with seeded jitter so rows never collide but nothing
// lines up. The first tiles of "Scene 3" and "Selects" are the scrubbed clips and sit exactly at
// the group origin, so the choreography can be derived from their rectangles.
// ---------------------------------------------------------------------------------------------
const RATIO = { w: 16 / 9, t: 9 / 16, s: 1, c: 2.39 } as const
type Aspect = keyof typeof RATIO
const GAP = 2.5
const FIXED_H = 14
const GROUPS: { x: number; y: number; w: number; label?: string; tiles: string; fixed?: number }[] = [
  { x: 12, y: 14, w: 92, label: 'Refs', tiles: 'stswtswstwst' },
  { x: 112, y: 10, w: 88, tiles: 'wcwwtwswww' },
  { x: 208, y: 8, w: 86, tiles: 'wwcwwswwcww' },
  { x: 8, y: 70, w: 96, tiles: 'cwwtwwswcww' },
  { x: 114, y: 66, w: 86, tiles: 'wsw' },
  { x: 116, y: 92, w: 80, label: 'Scene 3', tiles: 'wwcwwwcw', fixed: 1 },
  { x: 210, y: 80, w: 84, tiles: 'twwswcwwww' },
  { x: 8, y: 138, w: 98, tiles: 'wtwwswwcww' },
  { x: 112, y: 152, w: 84, tiles: 'wwcwsw' },
  { x: 200, y: 137, w: 94, label: 'Selects', tiles: 'wwwcwwwww', fixed: 2 },
]

type Rect = { x: number; y: number; w: number; h: number }
let seed = 11
// ponytail: a 32-bit LCG; the jitter only has to be deterministic and look uneven.
const rnd = () => (seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0) / 2 ** 32
const RECTS: Rect[] = []
const BINS: { label: string; r: Rect }[] = []
for (const g of GROUPS) {
  const first = RECTS.length
  let cx = Infinity
  let cy = g.y
  let base = 0
  let prev = 0
  g.tiles.split('').forEach((a, i) => {
    const fixed = i < (g.fixed ?? 0)
    let h = fixed ? FIXED_H : base * (0.8 + rnd() * 0.2)
    let w = h * RATIO[a as Aspect]
    if (cx + w > g.x + g.w) {
      cy += prev ? prev + GAP + rnd() * 3 : 0
      base = fixed ? FIXED_H : 9 + rnd() * 5
      h = fixed ? base : base * (0.8 + rnd() * 0.2)
      w = h * RATIO[a as Aspect]
      cx = g.x + (fixed ? 0 : rnd() * 8)
      prev = base
    }
    RECTS.push({ x: cx, y: cy + (fixed ? 0 : rnd() * (base - h + 2)), w, h })
    cx += w + GAP + (fixed ? 0 : rnd() * 4)
  })
  if (g.label) {
    const rs = RECTS.slice(first)
    const x0 = Math.min(...rs.map(r => r.x)) - 3
    const y0 = Math.min(...rs.map(r => r.y)) - 3
    const x1 = Math.max(...rs.map(r => r.x + r.w)) + 3
    const y1 = Math.max(...rs.map(r => r.y + r.h)) + 3
    BINS.push({ label: g.label, r: { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } })
  }
}
const SCENE3 = GROUPS.findIndex(g => g.label === 'Scene 3')
const SELECTS = GROUPS.findIndex(g => g.label === 'Selects')
const at = (group: number, i: number) => GROUPS.slice(0, group).reduce((n, g) => n + g.tiles.length, 0) + i
// Tile index → scrub clip index. S1 is scrubbed close up, S2 zoomed out, S3 close up again.
const SCRUB_AT = new Map([
  [at(SCENE3, 0), 0],
  [at(SELECTS, 1), 1],
  [at(SELECTS, 0), 2],
])
const S = [...SCRUB_AT.entries()].sort((a, b) => a[1] - b[1]).map(([i]) => RECTS[i])
let next = 0
const IMAGES = RECTS.map((_, i) => {
  const k = SCRUB_AT.get(i)
  return k === undefined ? ORDER[next++] : small(SCRUBS[k].poster)
})
if (RECTS.length !== 90 || new Set([...IMAGES, ...SCRUBS.flatMap(s => s.frames)]).size !== 108)
  throw new Error(`ProductionPerformance: ${RECTS.length} tiles, ${POOL.length + SCRUBS.length} images`)
for (const [i, a] of RECTS.entries()) {
  if (a.x < 2 || a.y < 2 || a.x + a.w > WORLD_W - 2 || a.y + a.h > WORLD_H - 2)
    throw new Error(`ProductionPerformance: tile ${i} leaves the world`)
  for (const b of RECTS.slice(i + 1))
    if (a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h)
      throw new Error(`ProductionPerformance: tiles overlap at ${a.x},${a.y}`)
}

// ---------------------------------------------------------------------------------------------
// Choreography. Points are in panel units unless named as world points.
// ---------------------------------------------------------------------------------------------
// Each scrubbed clip: hover span, sweep window, and where the sweep ends across the tile.
const SWEEPS: { hover: [number, number]; sweep: [number, number]; to: number; hide: number }[] = [
  { hover: [0.45, 5.7], sweep: [0.8, 2.0], to: 0.65, hide: 12.0 },
  { hover: [6.7, 10.6], sweep: [7.1, 7.9], to: 0.5, hide: 13.4 },
  { hover: [10.95, 12.4], sweep: [11.2, 12.0], to: 0.7, hide: 13.4 },
]

type Pt = { x: number; y: number }
const pt = (r: Rect, fx: number, fy: number): Pt => ({ x: r.x + r.w * fx, y: r.y + r.h * fy })
const mul = (p: Pt, k: number): Pt => ({ x: p.x * k, y: p.y * k })
const sub = (a: Pt, b: Pt): Pt => ({ x: a.x - b.x, y: a.y - b.y })
const P1 = pt(S[0], SWEEPS[0].to, 0.6) // world point under the Editor when the zoom-out starts
const P2 = pt(S[1], SWEEPS[1].to, 0.6) // world point under the Director when the zoom-in starts
const A0 = mul(P1, 2 / 3) // close-up A: world top-left at scale 1
const B0 = mul(P2, 2 / 3) // close-up B
const CA = mul(P1, 1 / 3) // Editor's cursor during the zoom-out
const CB = mul(P2, 1 / 3) // Director's cursor during the zoom-in
const DRAG = sub(B0, A0) // the hand's drag from close-up B back to close-up A (panel units)
const H_END: Pt = { x: 80, y: 46 }
const H_START = sub(H_END, DRAG)
if (H_START.x < 4 || H_START.y < 10 || CB.x > 88)
  throw new Error('ProductionPerformance: choreography leaves the panel')

const T = (s: number) => `${+((s / LOOP) * 100).toFixed(2)}%`
type Stop = [t: number, css: string, ease?: string]
const kf = (name: string, stops: Stop[]) =>
  `@keyframes ${name}{${stops.map(([t, css, ease]) => `${T(t)}{${css}${ease ? `;animation-timing-function:${ease}` : ''}}`).join('')}}`
// Step keyframes: `on` inside each [a, b] span, `off` elsewhere. Spans must not wrap the loop.
const flag = (name: string, on: string, off: string, spans: [number, number][]) =>
  kf(name, [
    [0, off],
    ...spans.flatMap<Stop>(([a, b]) => [
      [a, on],
      [b, off],
    ]),
    [LOOP, off],
  ])

const world = (t: Pt, s: number) =>
  `transform:translate(${(t.x / WORLD_W) * 100}%,${(t.y / WORLD_H) * 100}%) scale(${s})`
const W_A = world(mul(A0, -1), 1)
const W_OUT = world({ x: 0, y: 0 }, 1 / 3)
const W_B = world(mul(B0, -1), 1)
const worldStops = (zoom: string, drag: string): Stop[] => [
  [0, W_A],
  [2.6, W_A, zoom],
  [5.0, W_OUT],
  [8.2, W_OUT, zoom],
  [10.2, W_B],
  [12.0, W_B, drag],
  [13.3, W_A],
  [LOOP, W_A],
]

const EASE = 'cubic-bezier(.55,0,.25,1)'
const EASE_EARLY = 'cubic-bezier(.3,0,.1,1)'
const EASE_LATE = 'cubic-bezier(.75,0,.6,1)'
// A waypoint: be at (x, y) at time t. `bow` curves the move that ends here: y leads (+1) or
// trails (−1) x. `linear` is for scrubs and drags, where the hand moves at a steady speed.
type Way = { t: number; x: number; y: number; bow?: 1 | -1; linear?: boolean }
const cursorCSS = (id: string, ways: Way[]) => {
  const stops = (axis: 'x' | 'y'): Stop[] =>
    ways.map((w, i) => {
      const next = ways[i + 1]
      const ease = next?.linear ? 'linear' : axis === 'x' || !next?.bow ? EASE : next.bow > 0 ? EASE_EARLY : EASE_LATE
      return [
        w.t,
        axis === 'x' ? `transform:translateX(${w.x}%)` : `transform:translateY(${(w.y / PANEL_H) * 100}%)`,
        ease,
      ]
    })
  return kf(`perf-${id}-x`, stops('x')) + kf(`perf-${id}-y`, stops('y'))
}
const inA = (p: Pt) => sub(p, A0)
const inB = (p: Pt) => sub(p, B0)
const E0 = inA({ x: S[0].x - 6, y: P1.y + 3 })
const E1 = inA({ x: S[0].x + 3, y: P1.y })
const R0: Pt = { x: 72, y: 13 }
const R1 = mul({ x: S[1].x + 2, y: P2.y }, 1 / 3)
const R2 = inB({ x: S[2].x + 3, y: S[2].y + S[2].h * 0.55 })
const R3 = inB({ x: S[2].x + S[2].w * 0.7, y: S[2].y + S[2].h * 0.55 })
const H1: Pt = { x: 14, y: 50 }
const EDITOR: Way[] = [
  { t: 0, ...E0 },
  { t: 0.5, ...E1, bow: -1 },
  { t: 0.8, ...E1 },
  { t: 2.0, ...CA, linear: true },
  { t: 5.6, ...CA },
  { t: 7.0, x: 44, y: 55, bow: 1 },
  { t: 12.8, x: 44, y: 55 },
  { t: 13.8, ...E0, bow: -1 },
  { t: LOOP, ...E0 },
]
const DIRECTOR: Way[] = [
  { t: 0, ...R0 },
  { t: 5.4, ...R0 },
  { t: 6.8, ...R1, bow: -1 },
  { t: 7.1, ...R1 },
  { t: 7.9, ...CB, linear: true },
  { t: 10.5, ...CB },
  { t: 11.0, ...R2, bow: 1 },
  { t: 11.2, ...R2 },
  { t: 12.0, ...R3, linear: true },
  { t: 12.3, ...R3 },
  { t: 13.6, ...R0, bow: 1 },
  { t: LOOP, ...R0 },
]
const CLAUDE: Way[] = [
  { t: 0, ...H_END },
  { t: 0.6, ...H_END },
  { t: 2.0, ...H1, bow: 1 },
  { t: 10.6, ...H1 },
  { t: 11.6, ...H_START, bow: -1 },
  { t: 12.0, ...H_START },
  { t: 13.3, ...H_END },
  { t: LOOP, ...H_END },
]
const CURSORS = [
  { id: 'ed', name: 'Editor', ways: EDITOR },
  { id: 'dir', name: 'Director', ways: DIRECTOR },
  { id: 'cc', name: 'Claude Code', ways: CLAUDE, hand: true },
]

const scrubCSS = (k: number) => {
  const { hover, sweep, to, hide } = SWEEPS[k]
  const [a, b] = sweep
  const poster = `background-image:url("${small(SCRUBS[k].poster)}")`
  const frames = SCRUBS[k].frames.map<Stop>((f, i) => [a + ((b - a) * i) / 6, `background-image:url("${f}")`])
  const bar = (w: number, on: boolean) => `width:${w}%;opacity:${on ? 1 : 0}`
  return [
    kf(`perf-f${k}`, [[0, poster], ...frames, [b + 0.6, poster], [LOOP, poster]]),
    flag(`perf-h${k}`, 'outline-color:#fff', 'outline-color:transparent', [hover]),
    kf(`perf-b${k}`, [
      [0, bar(4, false)],
      [a, bar(4, true), 'linear'],
      [b, bar(to * 100, true)],
      [b + 0.6, bar(to * 100, true)],
      [b + 0.61, bar(4, false)],
      [LOOP, bar(4, false)],
    ]),
    flag(`perf-p${k}`, 'opacity:1', 'opacity:0', [[hover[0], b]]),
    flag(`perf-l${k}`, 'opacity:1', 'opacity:0', [[b, hide]]),
  ].join('')
}

// Left panel fps readout: one value per phase.
const FPS: [number, number][] = [
  [0, 13],
  [0.8, 9],
  [2.6, 5],
  [5.0, 12],
  [8.2, 4],
  [10.2, 11],
  [12.0, 7],
  [13.3, 13],
]
const FPS_VALUES = [...new Set(FPS.map(([, v]) => v))]
const fpsCSS = (v: number) =>
  flag(
    `perf-fps${v}`,
    'opacity:1',
    'opacity:0',
    FPS.flatMap(([t, val], i) => (val === v ? [[t, FPS[i + 1]?.[0] ?? LOOP] as [number, number]] : [])),
  )

const PLAY =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48'%3E%3Ccircle cx='24' cy='24' r='23' fill='rgba(0,0,0,.55)' stroke='rgba(255,255,255,.6)' stroke-width='2'/%3E%3Cpath d='M19 15l14 9-14 9z' fill='%23fff'/%3E%3C/svg%3E\")"

const CSS = `
${kf('perf-world', worldStops(EASE, EASE))}
${kf('perf-world-lag', worldStops('steps(6,end)', 'steps(4,end)'))}
${CURSORS.map(c => cursorCSS(c.id, c.ways)).join('')}
${SWEEPS.map((_, k) => scrubCSS(k)).join('')}
${FPS_VALUES.map(fpsCSS).join('')}
@keyframes perf-ph { 0%, 49.9% { opacity: 0 } 50%, 100% { opacity: 1 } }
@keyframes perf-spin { to { transform: rotate(360deg) } }

.perf-world { ${W_A}; animation: perf-world 14s infinite; }
.perf-lag .perf-world { animation-name: perf-world-lag; }
.perf-cur { animation: 14s infinite; }
/* A scrubbed clip: frames flip under the cursor, a hover ring and a position bar follow it. */
.perf-scrub { outline: 3px solid transparent; animation: var(--frames) 14s steps(1, end) infinite, var(--hover) 14s steps(1, end) infinite; }
.perf-scrub::after { content: ''; position: absolute; left: 0; bottom: 0; height: 4px; width: 4%; opacity: 0; background: #fff; animation: var(--bar) 14s infinite; }
/* Other canvases: a play button on hover, then a spinner that never resolves. */
.perf-lag .perf-scrub { animation-name: var(--hover); }
.perf-lag .perf-scrub::after { display: none; }
.perf-lag .perf-scrub::before { content: ''; position: absolute; inset: 0; margin: auto; width: 24%; aspect-ratio: 1; opacity: 0; background: ${PLAY} center / contain no-repeat; animation: var(--play) 14s steps(1, end) infinite; }
.perf-load { animation: 14s steps(1, end) infinite; }
.perf-load i { animation: perf-spin .9s linear infinite; }
.perf-fps { animation: 14s steps(1, end) infinite; }
/* A tile that has not loaded yet: grey cover and a spinner, no extra DOM. */
.perf-ph::before, .perf-ph::after {
  content: ''; position: absolute; inset: 0; opacity: 0;
  animation: perf-ph 14s steps(1, end) infinite; animation-delay: calc(var(--d) * -14s);
}
.perf-ph::before { background: #1a1a1a; border-radius: 3px; }
.perf-ph::after {
  margin: auto; width: 22%; aspect-ratio: 1; border-radius: 50%;
  border: 3px solid rgba(255,255,255,.12); border-top-color: rgba(255,255,255,.7);
  animation: perf-ph 14s steps(1, end) infinite, perf-spin .9s linear infinite;
}
@media (prefers-reduced-motion: reduce) {
  .perf-world, .perf-cur, .perf-scrub, .perf-lag .perf-scrub::before, .perf-scrub::after, .perf-load, .perf-load i, .perf-fps, .perf-ph::before, .perf-ph::after { animation: none; }
  .perf-fps { opacity: 0 } .perf-fps-${FPS[0][1]} { opacity: 1 }
}
`

const pc = (r: Rect) => ({
  left: `${(r.x / WORLD_W) * 100}%`,
  top: `${(r.y / WORLD_H) * 100}%`,
  width: `${(r.w / WORLD_W) * 100}%`,
  aspectRatio: `${r.w} / ${r.h}`,
})

function Cursor({ id, name, hand, ways }: { id: string; name: string; hand?: boolean; ways: Way[] }) {
  const Icon = hand ? Hand : MousePointer2
  return (
    <div
      className="perf-cur pointer-events-none absolute inset-0"
      style={{ transform: `translateX(${ways[0].x}%)`, animationName: `perf-${id}-x` }}
    >
      <div
        className="perf-cur absolute inset-0"
        style={{ transform: `translateY(${(ways[0].y / PANEL_H) * 100}%)`, animationName: `perf-${id}-y` }}
      >
        <Icon className="absolute left-0 top-0 size-4 fill-white text-black" strokeWidth={1.5} />
        <span className="absolute left-3.5 top-4 whitespace-nowrap rounded-[3px] bg-white px-1.5 py-[3px] text-[10px] font-medium leading-none text-black">
          {name}
        </span>
      </div>
    </div>
  )
}

function Panel({ laggy }: { laggy: boolean }) {
  return (
    <figure className={`min-w-0 ${laggy ? 'perf-lag' : ''}`}>
      <div className="relative aspect-[16/10] overflow-hidden bg-[#0a0a0a]">
        <div className="perf-world absolute left-0 top-0 aspect-[16/10] w-[300%] origin-top-left will-change-transform">
          {BINS.map(({ label, r }) => (
            <div key={label} className="absolute rounded-md border border-white/10" style={pc(r)}>
              <span className="absolute -top-5 left-1.5 text-[11px] font-medium tracking-wide text-white/50">
                {label}
              </span>
            </div>
          ))}
          {RECTS.map((r, i) => {
            const k = SCRUB_AT.get(i)
            const ph = laggy && k === undefined && i % 10 < 3
            return (
              <div
                key={IMAGES[i]}
                className={`absolute rounded-[3px] bg-cover bg-center ${k === undefined ? '' : 'perf-scrub'} ${ph ? 'perf-ph' : ''}`}
                style={{
                  ...pc(r),
                  backgroundImage: [IMAGES[i], ...(k === undefined || laggy ? [] : SCRUBS[k].frames)]
                    .map(u => `url(${u})`)
                    .join(','),
                  ...(ph ? ({ '--d': ((i * 37) % 100) / 100 } as React.CSSProperties) : undefined),
                  ...(k === undefined
                    ? undefined
                    : ({
                        '--frames': `perf-f${k}`,
                        '--bar': `perf-b${k}`,
                        '--hover': `perf-h${k}`,
                        '--play': `perf-p${k}`,
                      } as React.CSSProperties)),
                }}
              >
                {k !== undefined && laggy && (
                  <span
                    className="perf-load absolute inset-0 flex flex-col items-center justify-center gap-[5%] rounded-[3px] bg-black/60 text-[13px] font-medium text-white opacity-0"
                    style={{ animationName: `perf-l${k}` }}
                  >
                    <i className="aspect-square w-[16%] rounded-full border-[3px] border-white/20 border-t-white/90" />
                    Loading…
                  </span>
                )}
              </div>
            )
          })}
        </div>

        {CURSORS.map(c => (
          <Cursor key={c.id} {...c} />
        ))}

        <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-between bg-gradient-to-b from-black/80 to-transparent px-4 pb-8 pt-3 text-[12px] font-medium tracking-wide">
          <span className={`flex items-center gap-2 ${laggy ? 'text-white/60' : 'text-white'}`}>
            <span className={`size-1.5 rounded-full ${laggy ? 'bg-white/40' : 'bg-white'}`} />
            {laggy ? 'Other canvases' : 'Martini'}
          </span>
          {laggy ? (
            <span className="relative tabular-nums text-white/60">
              {FPS_VALUES.map((v, i) => (
                <span
                  key={v}
                  className={`perf-fps perf-fps-${v} ${i === 0 ? 'relative' : 'absolute inset-0 text-end'}`}
                  style={{ animationName: `perf-fps${v}` }}
                >
                  {v} fps
                </span>
              ))}
            </span>
          ) : (
            <span className="tabular-nums text-white">60 fps</span>
          )}
        </div>
      </div>
      <figcaption className="border-t border-white/[0.08] px-4 py-3.5 text-[13px] leading-relaxed text-white/45">
        {laggy ? (
          <>
            <span className="text-white/75">Three people elsewhere.</span> Hover does nothing, play never loads, the
            zoom and the pan land in steps.
          </>
        ) : (
          <>
            <span className="text-white">Three people in Martini.</span> Every clip scrubs the moment it is hovered;
            zoom and pan land on the frame at 60 fps.
          </>
        )}
      </figcaption>
    </figure>
  )
}

export function ProductionPerformance() {
  return (
    <div
      className="grid divide-y divide-white/[0.08] md:grid-cols-2 md:divide-x md:divide-y-0"
      role="img"
      aria-label="Three people zoom, scrub and pan a canvas of hundreds of clips: other canvases stall and drop frames, Martini scrubs every clip instantly at 60 fps"
    >
      <style>{CSS}</style>
      <Panel laggy />
      <Panel laggy={false} />
    </div>
  )
}
